import { useState, useEffect } from 'react';
import { FEATURED_MARKET_CODES } from '../../config/constants';
import { getCountry, getPropertyListingMode, formatUsd } from '../../data/countries';
import {
  getPropertyCardImage,
  getPropertyListerPrice,
  getPropertyPlatformPrice,
  formatPlatformPriceNote,
  isFirestoreListingId,
  offeringFormFromRecord,
  buildOfferingPropertyPatch,
} from '../../utils/properties';
import {
  findSubmissionForProperty,
  filterNumericInput,
  parseRentCostAmount,
  rentCostFormFromRecord,
  buildRentMonthlyCostsApiBody,
  submissionContentFormFromRecord,
  buildSubmissionContentApiBody,
  nearbyFormFromRecord,
  buildNearbyApiBody,
} from '../../utils/submissions';
import NearbyAmenitiesFields from '../forms/NearbyAmenitiesFields';
import { buildOfferingApiBody } from '../../utils/properties';
import apiClient from '../../utils/api';
import RentMonthlyCostsFields from '../forms/RentMonthlyCostsFields';
import AdminOfferingDetailsFields from '../forms/AdminOfferingDetailsFields';
import AdminReviewLogPanel from '../forms/AdminReviewLogPanel';

const AdminPropertyManageModal = ({ property, submissions, onClose, onSaved, onOpenSubmission, addToast }) => {
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const canManage = property && isFirestoreListingId(property.id);
  const isRentListing = getPropertyListingMode(property) === "rent";
  const linkedSubmission = findSubmissionForProperty(property, submissions);
  const heroImage = getPropertyCardImage(property);

  useEffect(() => {
    if (!property) return;
    const listerPrice = getPropertyListerPrice(property);
    const platformPrice = getPropertyPlatformPrice(property);
    const contentSeed = linkedSubmission || property;
    const content = submissionContentFormFromRecord(contentSeed);
    setEditForm({
      ...content,
      title: property.title || content.title || "",
      location: property.location || content.location || "",
      country: property.country || content.country || "US",
      type: property.type || content.type || "Residential",
      description: property.description || content.description || "",
      listerPrice,
      platformPrice,
      propertyValue: getPropertyPlatformPrice(property) || content.price || "",
      shares: property.shares ?? "",
      status: property.status || "funding",
      image: getPropertyCardImage(property),
      monthlyRent: property.monthlyRent != null ? String(property.monthlyRent) : content.monthlyRent || "",
      beds: property.beds != null ? String(property.beds) : content.beds || "",
      baths: property.baths != null ? String(property.baths) : content.baths || "",
      sqft: property.sqft != null ? String(property.sqft) : content.sqft || "",
      yearBuilt: property.yearBuilt != null ? String(property.yearBuilt) : content.yearBuilt || "",
      agentName: property.agentName || content.agentName || "",
      agentEmail: property.agentEmail || content.agentEmail || "",
      agentCompany: property.agentCompany || content.agentCompany || "",
      agentPhone: property.agentPhone || content.agentPhone || "",
      renovationNeeded: content.renovationNeeded || property.renovationNeeded || "no",
      renovationCost: content.renovationCost || (property.renovationCost != null ? String(property.renovationCost) : ""),
      renovationDesc: content.renovationDesc || property.renovationDesc || "",
      landBanking: content.landBanking || property.landBanking || "no",
      occupancy: property.occupancy != null ? String(property.occupancy) : content.occupancy || "",
      annualOperatingExpenses: property.annualOperatingExpenses != null
        ? String(property.annualOperatingExpenses)
        : content.annualOperatingExpenses || "",
      netOperatingIncome: property.netOperatingIncome != null
        ? String(property.netOperatingIncome)
        : content.netOperatingIncome || "",
      capRate: property.capRate != null ? String(property.capRate) : content.capRate || "",
      projectedReturn: property.projectedReturn != null
        ? String(property.projectedReturn)
        : content.projectedReturn || "",
      ...nearbyFormFromRecord(property),
      ...rentCostFormFromRecord(property),
      ...offeringFormFromRecord(property),
      tokenizationStatus: property.tokenizationStatus || "off",
      tokenizationLegalReady: property.tokenizationLegalReady === true,
      spvLegalName: property.spvLegalName || "",
    });
  }, [property, linkedSubmission?.id]);

  if (!property) return null;

  const update = (field, val) => setEditForm(prev => ({ ...prev, [field]: val }));
  const listerPrice = Number(editForm.listerPrice) || 0;
  const platformPrice = Number(editForm.platformPrice) || listerPrice;
  const priceNote = formatPlatformPriceNote(listerPrice, platformPrice);
  const hasMarkup = listerPrice > 0 && platformPrice > listerPrice;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!canManage) {
      addToast("Demo listings cannot be edited here. Only properties you published via admin/API.", "error");
      return;
    }
    setSaving(true);
    try {
      const imageUrl = (editForm.image || "").trim() || property.image;
      const basePayload = {
        title: editForm.title.trim(),
        location: editForm.location.trim(),
        country: editForm.country,
        type: editForm.type,
        description: editForm.description.trim() || editForm.title.trim(),
        image: imageUrl,
        gallery: [imageUrl]
      };

      const contentFields = {
        beds: editForm.beds,
        baths: editForm.baths,
        sqft: editForm.sqft,
        yearBuilt: editForm.yearBuilt,
        agentName: String(editForm.agentName || "").trim(),
        agentEmail: String(editForm.agentEmail || "").trim(),
        agentCompany: String(editForm.agentCompany || "").trim(),
        agentPhone: String(editForm.agentPhone || "").trim(),
        renovationNeeded: editForm.renovationNeeded,
        renovationCost: editForm.renovationCost,
        renovationDesc: String(editForm.renovationDesc || "").trim(),
        landBanking: editForm.landBanking,
        occupancy: editForm.occupancy,
        annualOperatingExpenses: editForm.annualOperatingExpenses,
        ...buildNearbyApiBody(editForm),
      };
      const adminFinancialFields = {
        netOperatingIncome: editForm.netOperatingIncome,
        capRate: editForm.capRate,
        projectedReturn: editForm.projectedReturn,
      };

      if (isRentListing) {
        const propertyValue = parseFloat(editForm.propertyValue);
        const monthlyRent = parseRentCostAmount(editForm.monthlyRent);
        if (!basePayload.title || !basePayload.location) {
          addToast("Title and location are required.", "error");
          return;
        }
        if (!monthlyRent) {
          addToast("Base monthly rent is required for rental listings.", "error");
          return;
        }
        const rentCostsBody = buildRentMonthlyCostsApiBody(editForm);
        await apiClient.updateProperty(property.id, {
          ...basePayload,
          listingMode: "rent",
          price: Number.isFinite(propertyValue) && propertyValue > 0 ? propertyValue : property.price,
          monthlyRent,
          ...contentFields,
          monthlyCosts: rentCostsBody.monthlyCosts ?? { items: [] }
        });
        addToast("Rental listing updated.");
      } else {
        const price = parseFloat(editForm.platformPrice);
        const shares = parseInt(editForm.shares, 10);
        if (!basePayload.title || !basePayload.location || !price || !shares) {
          addToast("Title, location, platform price, and shares are required.", "error");
          return;
        }
        await apiClient.updateProperty(property.id, {
          ...basePayload,
          price,
          shares,
          status: editForm.status,
          monthlyRent: editForm.monthlyRent,
          ...contentFields,
          ...adminFinancialFields,
          ...buildNearbyApiBody(editForm),
          ...buildOfferingPropertyPatch(editForm),
          tokenizationStatus: editForm.tokenizationStatus,
          tokenizationLegalReady: editForm.tokenizationLegalReady === true,
          spvLegalName: String(editForm.spvLegalName || "").trim(),
        });
        addToast(hasMarkup ? "Listing updated with platform price." : "Listing updated.");
      }

      if (linkedSubmission) {
        await apiClient.updateSubmission(
          linkedSubmission.id,
          buildSubmissionContentApiBody(editForm, linkedSubmission)
        );
      }

      onSaved();
      onClose();
    } catch (err) {
      addToast(err.message || "Update failed.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canManage) {
      addToast("Demo listings cannot be removed via API.", "error");
      return;
    }
    if (!window.confirm(`Remove "${property.title}" from the public site? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await apiClient.deleteProperty(property.id);
      addToast("Listing removed from the public site and moved to Deleted listings.");
      await onSaved();
      onClose();
    } catch (err) {
      addToast(err.message || "Delete failed.", "error");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="modal-overlay active" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal modal--manage-listing" onClick={(e) => e.stopPropagation()}>
        <div className="manage-modal__hero">
          {heroImage ? (
            <img className="manage-modal__hero-media" src={heroImage} alt="" />
          ) : (
            <div className="manage-modal__hero-placeholder" aria-hidden="true">
              <i className="fas fa-building"></i>
            </div>
          )}
          <div className="manage-modal__hero-text">
            <h2>{property.title || "Manage listing"}</h2>
            <p>
              {property.location || "—"}
              {isRentListing ? (
                <span className="admin-listing-badge admin-listing-badge--live" style={{ marginLeft: "0.5rem", verticalAlign: "middle" }}>
                  For rent
                </span>
              ) : (
                <> · {property.status || "funding"}</>
              )}
            </p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <div className="modal-body manage-modal__content">
          {!canManage && (
            <p style={{ color: "var(--warning)", marginBottom: "1rem", fontSize: "0.9rem" }}>
              <i className="fas fa-info-circle"></i> Built-in demo data. Publish via <strong>Add Property</strong> for editable Firestore listings.
            </p>
          )}
          <form onSubmit={handleSave}>
            {!isRentListing ? (
            <div className="manage-modal__section">
              <p className="manage-modal__section-title">Platform pricing</p>
              <div className="manage-modal__price-grid">
                <div className="manage-modal__price-readonly">
                  <label>Lister listed for</label>
                  <strong>{listerPrice ? formatUsd(listerPrice) : "—"}</strong>
                </div>
                <div className="manage-modal__price-platform">
                  <label htmlFor="manage-platform-price" style={{ display: "block", fontSize: "0.68rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-light)", marginBottom: "0.35rem" }}>
                    CoOwn platform listing (USD)
                  </label>
                  <input
                    id="manage-platform-price"
                    type="number"
                    min="1"
                    required
                    value={editForm.platformPrice}
                    onChange={(e) => update("platformPrice", e.target.value)}
                    disabled={!canManage}
                  />
                </div>
              </div>
              {priceNote ? (
                <p className={`manage-modal__price-note${hasMarkup ? " manage-modal__price-note--markup" : ""}`}>
                  <i className="fas fa-coins" style={{ marginRight: "0.35rem", color: "var(--primary)" }}></i>
                  {priceNote}
                </p>
              ) : null}
            </div>
            ) : (
            <div className="manage-modal__section">
              <p className="manage-modal__section-title">Rental pricing</p>
              <p style={{ fontSize: "0.85rem", color: "var(--text-light)", margin: "0 0 1rem", lineHeight: 1.55 }}>
                These fields appear on the public rent listing and Rent browse page.
              </p>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="manage-property-value">Total property value (USD)</label>
                  <input
                    id="manage-property-value"
                    type="text"
                    inputMode="decimal"
                    placeholder="Optional"
                    value={editForm.propertyValue}
                    onChange={(e) => update("propertyValue", filterNumericInput(e.target.value, { decimal: true }))}
                    disabled={!canManage}
                  />
                  {listerPrice > 0 ? (
                    <p style={{ fontSize: "0.78rem", color: "var(--text-light)", marginTop: "0.35rem" }}>
                      Lister ask when submitted: {formatUsd(listerPrice)}
                    </p>
                  ) : null}
                </div>
                <div className="form-group">
                  <label htmlFor="manage-monthly-rent">Base monthly rent (USD)</label>
                  <input
                    id="manage-monthly-rent"
                    type="text"
                    inputMode="decimal"
                    required
                    placeholder="e.g. 3200"
                    value={editForm.monthlyRent}
                    onChange={(e) => update("monthlyRent", filterNumericInput(e.target.value, { decimal: true }))}
                    disabled={!canManage}
                  />
                </div>
              </div>
            </div>
            )}

            {linkedSubmission ? (
              <div className="manage-modal__section" style={{ paddingBottom: "0.85rem" }}>
                <AdminReviewLogPanel
                  sub={linkedSubmission}
                  hint="Linked submission — full pipeline review is under Admin → Submissions."
                />
                {onOpenSubmission ? (
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    style={{ marginTop: "0.5rem" }}
                    onClick={() => onOpenSubmission(linkedSubmission.id)}
                  >
                    <i className="fas fa-external-link-alt"></i> Open submission review
                  </button>
                ) : null}
              </div>
            ) : (
              <p className="manage-modal__link-hint">
                <i className="fas fa-clipboard-list"></i> Admin review history appears when this property is linked to a lister submission, or under <strong>Admin → Submissions</strong> when reviewing the queue.
              </p>
            )}

            {!isRentListing ? (
            <div className="manage-modal__section">
              <p className="manage-modal__section-title">Tokenization</p>
              <p style={{ fontSize: "0.85rem", color: "var(--text-light)", margin: "0 0 1rem", lineHeight: 1.55 }}>
                <strong>Admin only</strong> — not shown on the public site. Legal template follows property country. ASA tokens mint only when status is <strong>Live</strong>, legal clearance is checked, and the offering is fully funded.
              </p>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="manage-tokenization-status">Tokenization status</label>
                  <select
                    id="manage-tokenization-status"
                    value={editForm.tokenizationStatus}
                    onChange={(e) => update("tokenizationStatus", e.target.value)}
                    disabled={!canManage}
                  >
                    <option value="off">Off</option>
                    <option value="pending_legal">Pending legal</option>
                    <option value="live">Live</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="manage-spv-legal-name">SPV legal name</label>
                  <input
                    id="manage-spv-legal-name"
                    placeholder="e.g. 123 Main St Property LLC"
                    value={editForm.spvLegalName}
                    onChange={(e) => update("spvLegalName", e.target.value)}
                    disabled={!canManage}
                  />
                </div>
              </div>
              <label className="manage-modal__checkbox" style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.5rem" }}>
                <input
                  type="checkbox"
                  checked={editForm.tokenizationLegalReady === true}
                  onChange={(e) => update("tokenizationLegalReady", e.target.checked)}
                  disabled={!canManage}
                />
                <span>Legal clearance complete (required before mint)</span>
              </label>
              {property.algorandAssetId ? (
                <p style={{ fontSize: "0.82rem", color: "var(--text-light)", marginTop: "0.75rem" }}>
                  Algorand ASA ID: <strong>{property.algorandAssetId}</strong>
                  {property.fundedClosedAt ? ` · Funded ${new Date(property.fundedClosedAt).toLocaleDateString()}` : ""}
                </p>
              ) : null}
            </div>
            ) : null}

            <div className="manage-modal__section manage-modal__form-grid">
              <p className="manage-modal__section-title">Listing details</p>
              <div className="form-row">
                <div className="form-group"><label>Title</label><input required value={editForm.title} onChange={(e) => update("title", e.target.value)} disabled={!canManage} /></div>
                <div className="form-group"><label>Location</label><input required value={editForm.location} onChange={(e) => update("location", e.target.value)} disabled={!canManage} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Country</label>
                  <select value={editForm.country} onChange={(e) => update("country", e.target.value)} disabled={!canManage}>
                    {FEATURED_MARKET_CODES.map(code => <option key={code} value={code}>{getCountry(code).name}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Type</label>
                  <select value={editForm.type} onChange={(e) => update("type", e.target.value)} disabled={!canManage}>
                    {["Residential", "Commercial", "Vacation", "Land", "Mixed-Use"].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              {!isRentListing ? (
              <div className="form-row">
                <div className="form-group"><label>Shares</label><input type="number" min="1" required value={editForm.shares} onChange={(e) => update("shares", e.target.value)} disabled={!canManage} /></div>
                <div className="form-group"><label>Status</label>
                  <select value={editForm.status} onChange={(e) => update("status", e.target.value)} disabled={!canManage}>
                    <option value="funding">Funding</option>
                    <option value="funded">Funded</option>
                  </select>
                </div>
              </div>
              ) : null}
              <div className="form-row">
                <div className="form-group"><label>Bedrooms</label><input type="text" inputMode="numeric" value={editForm.beds} onChange={(e) => update("beds", filterNumericInput(e.target.value))} disabled={!canManage} /></div>
                <div className="form-group"><label>Bathrooms</label><input type="text" inputMode="numeric" value={editForm.baths} onChange={(e) => update("baths", filterNumericInput(e.target.value))} disabled={!canManage} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Square feet</label><input type="text" inputMode="numeric" value={editForm.sqft} onChange={(e) => update("sqft", filterNumericInput(e.target.value))} disabled={!canManage} /></div>
                <div className="form-group"><label>Year built</label><input type="text" inputMode="numeric" value={editForm.yearBuilt} onChange={(e) => update("yearBuilt", filterNumericInput(e.target.value))} disabled={!canManage} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Agent name</label><input value={editForm.agentName} onChange={(e) => update("agentName", e.target.value)} disabled={!canManage} /></div>
                <div className="form-group"><label>Agent email</label><input type="email" value={editForm.agentEmail} onChange={(e) => update("agentEmail", e.target.value)} disabled={!canManage} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Company</label><input value={editForm.agentCompany} onChange={(e) => update("agentCompany", e.target.value)} disabled={!canManage} /></div>
                <div className="form-group"><label>Phone</label><input value={editForm.agentPhone} onChange={(e) => update("agentPhone", e.target.value)} disabled={!canManage} /></div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Renovation needed?</label>
                  <select value={editForm.renovationNeeded} onChange={(e) => update("renovationNeeded", e.target.value)} disabled={!canManage}>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
                {editForm.renovationNeeded === "yes" ? (
                  <div className="form-group">
                    <label>Est. renovation cost ($)</label>
                    <input type="text" inputMode="decimal" value={editForm.renovationCost} onChange={(e) => update("renovationCost", filterNumericInput(e.target.value, { decimal: true }))} disabled={!canManage} />
                  </div>
                ) : (
                  <div className="form-group">
                    <label>Land banking?</label>
                    <select value={editForm.landBanking} onChange={(e) => update("landBanking", e.target.value)} disabled={!canManage}>
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                )}
              </div>
              {editForm.renovationNeeded === "yes" ? (
                <>
                  <div className="form-group"><label>Renovation notes</label><textarea rows={2} value={editForm.renovationDesc} onChange={(e) => update("renovationDesc", e.target.value)} disabled={!canManage} /></div>
                  <div className="form-group">
                    <label>Land banking?</label>
                    <select value={editForm.landBanking} onChange={(e) => update("landBanking", e.target.value)} disabled={!canManage}>
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                </>
              ) : null}
              {linkedSubmission ? (
                <p style={{ fontSize: "0.8rem", color: "var(--text-light)", margin: "0.35rem 0 0", lineHeight: 1.5 }}>
                  Saving also updates the linked submission so lister and live listing stay in sync.
                </p>
              ) : null}
              <div className="form-group"><label>Description</label><textarea rows={2} value={editForm.description} onChange={(e) => update("description", e.target.value)} disabled={!canManage} /></div>
              <div className="form-group"><label>Cover image URL</label><input value={editForm.image} onChange={(e) => update("image", e.target.value)} disabled={!canManage} /></div>
            </div>

            <div className="manage-modal__section manage-modal__form-grid">
              <p className="manage-modal__section-title">Nearby landmarks & amenities</p>
              <p style={{ fontSize: "0.85rem", color: "var(--text-light)", margin: "0 0 1rem", lineHeight: 1.55 }}>
                Shown on the public Nearby tab. Use travel times like &quot;15 min (JFK)&quot; or &quot;5 min&quot;.
              </p>
              <NearbyAmenitiesFields form={editForm} update={update} compact />
            </div>

            {!isRentListing ? (
              <div className="manage-modal__section manage-modal__form-grid">
                <p className="manage-modal__section-title">Lister financial inputs</p>
                <p style={{ fontSize: "0.85rem", color: "var(--text-light)", margin: "0 0 1rem", lineHeight: 1.55 }}>
                  Monthly rent, occupancy, and expense estimates come from the lister. CoOwn-reviewed outputs below are shown to investors.
                </p>
                <div className="form-row">
                  <div className="form-group">
                    <label>Monthly rent ($)</label>
                    <input type="text" inputMode="decimal" value={editForm.monthlyRent} onChange={(e) => update("monthlyRent", filterNumericInput(e.target.value, { decimal: true }))} disabled={!canManage} />
                  </div>
                  <div className="form-group">
                    <label>Occupancy (%)</label>
                    <input type="text" inputMode="decimal" value={editForm.occupancy} onChange={(e) => update("occupancy", filterNumericInput(e.target.value, { decimal: true }))} disabled={!canManage} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Est. annual operating expenses ($)</label>
                  <input type="text" inputMode="decimal" value={editForm.annualOperatingExpenses} onChange={(e) => update("annualOperatingExpenses", filterNumericInput(e.target.value, { decimal: true }))} disabled={!canManage} />
                </div>
                <p className="manage-modal__section-title" style={{ marginTop: "1.25rem" }}>CoOwn reviewed financials</p>
                <div className="form-row">
                  <div className="form-group">
                    <label>Net operating income ($)</label>
                    <input type="text" inputMode="decimal" value={editForm.netOperatingIncome} onChange={(e) => update("netOperatingIncome", filterNumericInput(e.target.value, { decimal: true }))} disabled={!canManage} />
                  </div>
                  <div className="form-group">
                    <label>Cap rate (%)</label>
                    <input type="text" inputMode="decimal" value={editForm.capRate} onChange={(e) => update("capRate", filterNumericInput(e.target.value, { decimal: true }))} disabled={!canManage} />
                  </div>
                  <div className="form-group">
                    <label>Projected return (%)</label>
                    <input type="text" inputMode="decimal" value={editForm.projectedReturn} onChange={(e) => update("projectedReturn", filterNumericInput(e.target.value, { decimal: true }))} disabled={!canManage} />
                  </div>
                </div>
              </div>
            ) : null}

            {isRentListing ? (
              <div className="manage-modal__section manage-modal__form-grid">
                <p className="manage-modal__section-title">Monthly costs breakdown</p>
                <p style={{ fontSize: "0.85rem", color: "var(--text-light)", margin: "0 0 1rem", lineHeight: 1.55 }}>
                  Optional line items shown to renters on the property page (electricity, internet, insurance, etc.).
                </p>
                <RentMonthlyCostsFields
                  form={editForm}
                  setForm={setEditForm}
                  disabled={!canManage}
                  idPrefix={`manage-rent-costs-${property?.id || "p"}`}
                />
              </div>
            ) : (
              <div className="manage-modal__section manage-modal__form-grid">
                <p className="manage-modal__section-title">Offering &amp; timeline</p>
                <p style={{ fontSize: "0.85rem", color: "var(--text-light)", margin: "0 0 1rem" }}>
                  Shown on the public property page (Offering overview card and funding timeline).
                </p>
                <AdminOfferingDetailsFields
                  form={editForm}
                  setForm={setEditForm}
                  disabled={!canManage}
                  idPrefix={`manage-${property?.id || "p"}`}
                />
              </div>
            )}

            <div className="manage-modal__footer">
              <button
                type="button"
                className="manage-modal__footer-remove"
                disabled={!canManage || deleting}
                onClick={handleDelete}
              >
                <i className="fas fa-eye-slash" aria-hidden="true"></i>
                {deleting ? "Removing…" : "Remove from site"}
              </button>
              <div className="manage-modal__footer-actions">
                <button type="button" className="manage-modal__footer-btn manage-modal__footer-btn--cancel" onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="manage-modal__footer-btn manage-modal__footer-btn--save"
                  disabled={!canManage || saving}
                >
                  <i className={`fas fa-${saving ? "spinner fa-spin" : "check"}`} aria-hidden="true"></i>
                  {saving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminPropertyManageModal;
